import React, { useState } from "react";
import { Alert, Chip } from "@mui/material";
import { CardGiftcard as GiftIcon } from "@mui/icons-material";
import FreebieDownloadForm, {
  FreebieFormData,
} from "../FreebieDownloadForm/FreebieDownloadForm";
import ProductImage from "../../../../components/common/ProductImage/ProductImage";
import freebieService from "../../../../lib/freebieService";
import styles from "../../Product.module.css"; // Import the main Product CSS

interface FreebieProductViewProps {
  product: any;
  mainImage: string | null;
  thumbnails: any[];
  onThumbnailClick: (imageUrl: string) => void;
}

const FreebieProductView: React.FC<FreebieProductViewProps> = ({
  product,
  mainImage,
  thumbnails,
  onThumbnailClick,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  const handleDownload = async (formData: FreebieFormData): Promise<void> => {
    setIsDownloading(true);

    try {
      // Create lead using the freebie service
      await freebieService.createLead({
        productId: product.id.toString(),
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        source: "freebie_download",
      });

      // Trigger download
      if (product.downloadUrl) {
        // Create a temporary link to trigger download
        const link = document.createElement("a");
        link.href = product.downloadUrl;
        link.download = product.downloadFileName || `${product.name}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // If no direct download URL, get download URL from service
        const downloadResponse = await freebieService.getDownloadUrl(
          product.id.toString(),
          formData.email
        );
        if (downloadResponse.downloadUrl) {
          const link = document.createElement("a");
          link.href = downloadResponse.downloadUrl;
          link.download = product.downloadFileName || `${product.name}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }

      setDownloadSuccess(true);

      // Track download event
      freebieService.trackEvent("freebie_download", product.id.toString(), {
        product_name: product.name,
        partner_name: product.partner?.name || "",
        user_email: formData.email,
      });

      console.log("Freebie downloaded successfully:", {
        productId: product.id,
        productName: product.name,
        userEmail: formData.email,
      });
    } catch (error) {
      console.error("Error downloading freebie:", error);
      throw error;
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={styles.product}>
      {/* Product Images - Same as regular product */}
      <div className={styles.productImage}>
        <div className={styles.mainImageContainer}>
          <ProductImage
            className={styles.mainImage}
            src={mainImage || product.images?.[0]?.medium}
            alt={product.name}
          />
        </div>
        <div className={styles.thumbnails}>
          {thumbnails.map((img: any, index: number) => (
            <ProductImage
              key={index}
              className={styles.thumbnail}
              src={img.small || undefined}
              alt={`Thumbnail ${index + 1}`}
              onClick={() => onThumbnailClick(img.small)}
            />
          ))}
        </div>
      </div>

      {/* Product Info - Same layout as regular product */}
      <div className={styles.productInfo}>
        {/* Freebie Badge - positioned like partner name */}
        <div className={styles.productBrand}>
          <Chip
            icon={<GiftIcon />}
            label="Freebie Gratis"
            color="success"
            size="small"
            style={{
              fontSize: "12px",
              height: "24px",
              marginBottom: "8px",
            }}
          />
          {product.partner?.name && (
            <span
              style={{ marginLeft: "8px", fontSize: "14px", color: "#666" }}
            >
              Por {product.partner.name}
            </span>
          )}
        </div>

        {/* Product Name - Same styling */}
        <h1 className={styles.productName}>{product.name}</h1>

        {/* Price section replaced with "Gratis" */}
        <div className={styles.priceDisplay}>
          <p
            className={styles.regularPrice}
            style={{ color: "#4caf50", fontWeight: "bold" }}
          >
            ¡Completamente GRATIS!
          </p>
        </div>

        {/* Product Description - Same styling */}
        {product.description && (
          <p style={{ marginBottom: "20px", lineHeight: "1.6" }}>
            {product.description}
          </p>
        )}

        {/* Success Message */}
        {downloadSuccess && (
          <Alert severity="success" style={{ marginBottom: "16px" }}>
            ¡Descarga exitosa! Revisa tu carpeta de descargas.
          </Alert>
        )}

        {/* Freebie Form - Replaces the quantity/cart section */}
        <div
          className={styles.quantityAddToCartContainer}
          style={{ position: "relative" }}
        >
          <FreebieDownloadForm
            product={product}
            onDownload={handleDownload}
            isLoading={isDownloading}
          />
        </div>

        {/* Additional Info - Same styling as stock availability */}
        <div className={styles.stockAvailability}>
          <div className={styles.stockAvailableText}>
            <div className={styles.stockIcon}>
              <GiftIcon
                style={{ color: "#4caf50", width: "24px", height: "24px" }}
              />
            </div>
            <p>
              💡 <strong>Este es un freebie gratuito</strong> - No necesitas
              agregar nada al carrito. Solo completa tus datos y descarga
              instantáneamente.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreebieProductView;
